
import { useParams } from "wouter";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { useEffect } from "react";
import { Helmet } from "react-helmet"; // Assuming react-helmet or similar is available, or we just update document.title
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, ShieldCheck, User } from "lucide-react";

export default function CityLanding() {
    const { country, city } = useParams();

    // Normalize params for query
    const countryCode = country?.toUpperCase();
    const cityName = city ? decodeURIComponent(city) : "";

    // Fetch City Data (mocked or real if we implemented the query)
    // For now, we'll assume we can search ads by location string or we'd need a specific query
    // Let's use a general semantic search or filter if specific city query isn't ready.
    // Actually, we should probably implement a specific query for this context.
    // But let's use the existing "ads" query or search if available. 
    // Looking at schema, ads have "location".

    // We'll define a new query component-side or reuse. 
    // Let's assume we want to query by generic location string for now.
    const ads = useQuery(api.ads.searchAds, {
        query: cityName,
        filterDetails: { country: countryCode }
    });

    // SEO: Update Title/Description
    useEffect(() => {
        if (cityName && countryCode) {
            document.title = `Verified Personal Ads in ${cityName}, ${countryCode} | Listo`;
            // Update meta description if possible
            const metaDesc = document.querySelector('meta[name="description"]');
            if (metaDesc) {
                metaDesc.setAttribute("content", `Find verified personal ads, connections, and services in ${cityName}. Safe, secure, and age-verified marketplace.`);
            }
        }
    }, [cityName, countryCode]);

    return (
        <div className="container mx-auto px-4 py-8">
            {/* SEO Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                    Verified Personals in {cityName}, {countryCode}
                </h1>
                <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">
                    Connect with real, age-verified people in {cityName}.
                    Safe. Secure. No Bots.
                </p>
            </div>

            {/* Trust Signals */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-900">
                    <ShieldCheck className="w-8 h-8 text-green-600" />
                    <div>
                        <h3 className="font-semibold text-green-800 dark:text-green-300">Identity Verified</h3>
                        <p className="text-sm text-green-700 dark:text-green-400">Every user is ID checked (World ID / Gov ID).</p>
                    </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-900">
                    <User className="w-8 h-8 text-blue-600" />
                    <div>
                        <h3 className="font-semibold text-blue-800 dark:text-blue-300">Real Humans Only</h3>
                        <p className="text-sm text-blue-700 dark:text-blue-400">Sybil-resistant. No AI bots allowed.</p>
                    </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-900">
                    <MapPin className="w-8 h-8 text-purple-600" />
                    <div>
                        <h3 className="font-semibold text-purple-800 dark:text-purple-300">Local to {cityName}</h3>
                        <p className="text-sm text-purple-700 dark:text-purple-400">Geolocation verified listings.</p>
                    </div>
                </div>
            </div>

            {/* Listings Grid */}
            {ads === undefined ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="h-64 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse" />
                    ))}
                </div>
            ) : ads.length === 0 ? (
                <div className="text-center py-20 bg-gray-50 dark:bg-gray-900 rounded-xl">
                    <h2 className="text-xl font-semibold mb-2">No listings yet in {cityName}</h2>
                    <p className="text-muted-foreground mb-6">Be the first to post a verified ad in this city!</p>
                    <Button size="lg" className="bg-primary text-primary-foreground">
                        Post an Ad in {cityName}
                    </Button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {ads.map((ad: any) => (
                        <Card key={ad._id} className="overflow-hidden hover:shadow-md transition-shadow">
                            <div className="aspect-video bg-gray-200 relative">
                                {ad.images?.[0] && <img src={ad.images[0]} alt={ad.title} className="w-full h-full object-cover" />}
                                {ad.isPersonalConnection && <Badge className="absolute top-2 right-2 bg-pink-500">Personal</Badge>}
                            </div>
                            <CardHeader className="pb-2">
                                <h3 className="font-semibold text-lg line-clamp-1">{ad.title}</h3>
                                <div className="flex items-center text-xs text-muted-foreground gap-1">
                                    <MapPin className="w-3 h-3" /> {ad.location || cityName}
                                </div>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm text-muted-foreground line-clamp-2">{ad.description}</p>
                            </CardContent>
                            <CardFooter className="pt-0">
                                <Button variant="outline" className="w-full">View Details</Button>
                            </CardFooter>
                        </Card>
                    ))}
                </div>
            )}

            {/* City SEO Footer Content */}
            <div className="mt-16 pt-8 border-t border-gray-200 dark:border-gray-800 text-sm text-muted-foreground">
                <p>
                    Looking for personal ads in {cityName}? Listo is the safest way to meet people in {countryCode}.
                    Unlike Craigslist or unrestrained platforms, we verify every single user using advanced World ID and government identity checks
                    to ensure you are talking to a real, adult human. Browse our classifieds for {cityName} dating,
                    friendships, and community connections today.
                </p>
            </div>
        </div>
    );
}
